import { type FC, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { Modal } from '../../../components/Modal/Modal'
import { Spinner } from '../../../components/Spinner/Spinner'
import { useAccount } from '../../../common/useAccount'
import { firstApiErrorMessage, isFetchBaseQueryError } from '../../../models/ApiErrors'
import { type Booking, type SchedulingEventType } from '../../../models/Scheduling'
import { type TemplateValue } from '../../../models/TemplateLibrary'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useSendReminderMutation } from '../../../store/scheduling/scheduling'
import { addToast } from '../../../store/toast/toastSlice'
import { useGetUsersQuery } from '../../../store/user/user'
import { currentFirst } from '../../Setup/Scheduling/sections/EmailTemplatesSetup/currentFirst'
import { TemplatePicker, TemplatePreviewPanel } from '../../Setup/Scheduling/sections/EmailTemplatesSetup/TemplatePicker'
import { TemplateScopeBadge } from '../../Setup/Templates/TemplateScopeBadge'
import { useTemplateCreators } from '../../Setup/Templates/useTemplateCreators'
import { clientDisplayName } from '../shared/schedulingDisplay'
import { FollowUpTemplateSelect } from './FollowUpTemplateSelect'
import { followUpPreviewValues } from './followUpPreviewValues'
import { needsFixing, reminderRecipients } from './followUpTemplates'
import { useBookingLocation } from './useBookingLocation'
import { useFollowUpTemplates } from './useFollowUpTemplates'

const SEND_FAILED = 'Something went wrong sending this reminder. Try again or contact support.'
const TEMPLATE_GONE = "This template isn't available anymore. Choose another one."
const ONLY_THIS_REMINDER = 'Only changes this reminder. Automatic reminders keep their template.'
const LINK_CLASS = 'p-0 border-0 bg-transparent paragraph-medium zocks-text-primary'

const NO_VALUES: Record<string, string> = {}

type View = 'send' | 'compare' | 'preview'

const isTemplateGone = (error: unknown) =>
  isFetchBaseQueryError(error) && (error.status === 400 || error.status === 404) && /template/i.test(firstApiErrorMessage(error) ?? '')

const sendErrorMessage = (error: unknown): string => {
  if (isFetchBaseQueryError(error) && error.status === 409) {
    return firstApiErrorMessage(error) ?? SEND_FAILED
  }
  return isTemplateGone(error) ? TEMPLATE_GONE : SEND_FAILED
}

interface SendReminderModalProps {
  booking: Booking
  eventType?: SchedulingEventType
  onHide: () => void
}

export const SendReminderModal: FC<SendReminderModalProps> = ({ booking, eventType, onHide }) => {
  const dispatch = useAppDispatch()
  const { trackingClient } = useTracking()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const { account } = useAccount()
  const { templates, preselected, isLocked, isLoading, refetchTemplates } = useFollowUpTemplates()
  const { data: owners } = useGetUsersQuery({ accountId: authenticatedUser.accountId, ids: [booking.userId], pageRequest: { page: 0, size: 1 } })
  const usersById = useTemplateCreators(templates)
  const location = useBookingLocation(booking)
  const [sendReminder, { isLoading: isSending }] = useSendReminderMutation()
  const [view, setView] = useState<View>('send')
  const [chosenId, setChosenId] = useState<string>()
  const [comparedId, setComparedId] = useState<string>()
  const [previewIndex, setPreviewIndex] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const recipients = reminderRecipients(booking)
  const chosen = templates.find((template) => template.id === chosenId)
  const selected = chosen ?? preselected
  const compared = templates.find((template) => template.id === comparedId) ?? selected
  const previewClient = recipients[previewIndex] ?? recipients[0]
  const owner = owners?.data[0]
  const values =
    eventType != null && previewClient != null ? followUpPreviewValues({ booking, client: previewClient, eventType, owner, account, location }) : NO_VALUES
  const canChoose = !isLocked && templates.length > 1 && selected != null
  const recipientLabel = recipients.length === 1 ? clientDisplayName(recipients[0]) : `${recipients.length} people`
  const meetingName = eventType?.name ?? booking.title ?? 'the meeting'

  const handleSend = async () => {
    setError(null)
    const result = await sendReminder({
      accountId: authenticatedUser.accountId,
      bookingId: booking.bookingId,
      templateId: chosen?.id,
      suppressGlobalErrorToast: true,
    })
    if ('error' in result) {
      setError(sendErrorMessage(result.error))
      if (isTemplateGone(result.error)) {
        void refetchTemplates()
      }
      return
    }
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_REMINDER_SENT, eventDetails: { bookingId: booking.bookingId } })
    dispatch(addToast({ variant: 'success', title: `Reminder sent to ${recipientLabel}` }))
    onHide()
  }

  const openCompare = () => {
    setComparedId(selected?.id)
    setView('compare')
  }

  const previewFor =
    recipients.length > 1 ? (
      <Form.Group className='d-flex align-items-center gap-2 flex-shrink-0' controlId='send-reminder-preview-for'>
        <Form.Label className='mb-0 paragraph-small zocks-text-gray-600 text-nowrap'>Preview for</Form.Label>
        <Form.Select size='sm' value={previewIndex} onChange={(event) => setPreviewIndex(Number(event.currentTarget.value))}>
          {recipients.map((client, index) => (
            <option key={client.email} value={index}>
              {clientDisplayName(client)}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
    ) : null

  if (view === 'compare' && compared != null) {
    return (
      <Modal
        show
        size='xl'
        fullscreen='lg-down'
        scrollable
        onHide={onHide}
        header={
          <div className='d-flex flex-column'>
            <h4 className='mb-0'>Choose a reminder template</h4>
            <span className='paragraph-medium zocks-text-gray-600'>{ONLY_THIS_REMINDER}</span>
          </div>
        }
        body={
          <TemplatePicker
            templates={currentFirst(templates, selected?.id)}
            selected={compared}
            currentTemplateId={selected?.id}
            currentLabel='Selected'
            usersById={usersById}
            values={values}
            actions={previewFor}
            isDisabled={needsFixing}
            onSelect={setComparedId}
          />
        }
        footer={
          <div className='d-flex gap-2'>
            <Button type='button' variant='secondary' onClick={() => setView('send')}>
              Back
            </Button>
            <Button
              type='button'
              variant='primary'
              onClick={() => {
                setChosenId(compared.id)
                setView('send')
              }}
            >
              Select template
            </Button>
          </div>
        }
      />
    )
  }

  if (view === 'preview' && selected != null) {
    return (
      <Modal
        show
        size='xl'
        fullscreen='lg-down'
        scrollable
        onHide={onHide}
        header={
          <div className='d-flex flex-column'>
            <h4 className='mb-0'>Reminder email</h4>
            <span className='paragraph-medium zocks-text-gray-600'>
              This is the email {previewClient != null ? clientDisplayName(previewClient) : 'the client'} gets.
            </span>
          </div>
        }
        body={<TemplatePreviewPanel template={selected} values={values} actions={previewFor} />}
        footer={
          <Button type='button' variant='secondary' onClick={() => setView('send')}>
            Back
          </Button>
        }
      />
    )
  }

  return (
    <Modal
      show
      backdrop={isSending ? 'static' : true}
      keyboard={!isSending}
      onHide={isSending ? () => undefined : onHide}
      header={<h4 className='mb-0'>Send reminder</h4>}
      body={
        <div className='d-flex flex-column gap-3'>
          <p className='mb-0 paragraph-medium zocks-text-gray-900'>
            Asks {recipientLabel} to pick a time for {meetingName}.
          </p>
          <div className='d-flex flex-column gap-1'>
            <span className='paragraph-medium fw-semibold zocks-text-gray-900'>To</span>
            <div className='d-flex flex-column gap-1 rounded-2 px-3 py-2 zocks-bg-color-gray-100'>
              {recipients.map((client) => (
                <span key={client.email} className='paragraph-medium zocks-text-gray-900'>
                  {clientDisplayName(client)} <span className='ms-2 paragraph-small zocks-text-gray-600'>{client.email}</span>
                </span>
              ))}
            </div>
            {recipients.length > 1 ? <span className='paragraph-small zocks-text-gray-600'>Each person gets their own email.</span> : null}
          </div>
          <div className='d-flex flex-column gap-1'>
            <div className='d-flex justify-content-between align-items-center'>
              <span className='paragraph-medium fw-semibold zocks-text-gray-900'>Template</span>
              {!isLoading && selected != null ? (
                canChoose ? (
                  <button type='button' className={LINK_CLASS} disabled={isSending} onClick={openCompare}>
                    Compare templates
                  </button>
                ) : (
                  <button type='button' className={LINK_CLASS} disabled={isSending} onClick={() => setView('preview')}>
                    Preview email
                  </button>
                )
              ) : null}
            </div>
            {isLoading ? (
              <div className='d-flex justify-content-center py-2'>
                <Spinner />
              </div>
            ) : canChoose ? (
              <>
                <FollowUpTemplateSelect templates={templates} selected={selected} disabled={isSending} onSelect={setChosenId} />
                <span className='paragraph-small zocks-text-gray-600'>{ONLY_THIS_REMINDER}</span>
              </>
            ) : (
              <ReadOnlyTemplate template={selected} />
            )}
          </div>
          {error != null ? (
            <div role='alert' className='d-flex align-items-center gap-2 rounded-2 px-3 py-2 zocks-bg-color-danger-background zocks-text-danger-200'>
              <i className='bi bi-exclamation-triangle-fill' aria-hidden='true' />
              <span className='paragraph-medium'>{error}</span>
            </div>
          ) : null}
        </div>
      }
      footer={
        <div className='d-flex gap-2'>
          <Button type='button' variant='secondary' disabled={isSending} onClick={onHide}>
            Cancel
          </Button>
          <Button type='button' variant='primary' loading={isSending} disabled={isLoading || recipients.length === 0} onClick={handleSend}>
            Send reminder
          </Button>
        </div>
      }
    />
  )
}

const ReadOnlyTemplate: FC<{ template?: TemplateValue }> = ({ template }) => (
  <div className='d-flex align-items-center gap-2 rounded-2 px-3 py-2 zocks-bg-color-gray-100 paragraph-medium zocks-text-gray-900'>
    {template?.name ?? 'Zocks default'}
    {template != null ? <TemplateScopeBadge scope={template.scope} /> : null}
  </div>
)
